import annotator from 'annotator';
import TextSelector from './annotator';

import {
  AnnotationContainer,
  AnnotatorContainer,
} from './containers';
import { encloseAdjacentWhitespaces, getEndSpaceCounts } from './utils';


class AnnotatorModule {
  constructor(app, element, uuid, gocheckVersion, isEditable, onAnnotationChange) {
    this.app = app;
    this.element = element;
    this.uuid = uuid;
    this.gocheckVersion = gocheckVersion;
    this.isEditable = isEditable;
    this.onAnnotationChange = onAnnotationChange;

    this.ui = {
      highlighter: null,
      interactionPoint: null,
      textselector: null,
      viewer: null,
      editor: null
    };

    this.start = this.start.bind(this);
    this.destroy = this.destroy.bind(this);
    this.annotationsLoaded = this.annotationsLoaded.bind(this);
    this.annotationCreated = this.annotationCreated.bind(this);
    this.annotationUpdated = this.annotationUpdated.bind(this);
    this.annotationDeleted = this.annotationDeleted.bind(this);
    this.annotationClosed = this.annotationClosed.bind(this);
    this.beforeAnnotationCreated = this.beforeAnnotationCreated.bind(this);
    this.beforeAnnotationUpdated = this.beforeAnnotationUpdated.bind(this);

    this._createEditor = this._createEditor.bind(this);
    this._createHighlighter = this._createHighlighter.bind(this);
    this._createTextSelector = this._createTextSelector.bind(this);
    this._createViewer = this._createViewer.bind(this);
  }

  start() {
    this.ui.highlighter = this._createHighlighter();

    this.ui.viewer = this._createViewer();
    this.ui.viewer.attach();

    if (this.isEditable) {
      this.ui.textselector = this._createTextSelector();
      this.ui.editor = this._createEditor();
      this.ui.editor.attach();
    }
  }

  _createHighlighter() {
    return new annotator.ui.highlighter.Highlighter(this.element);
  }

  _createViewer() {
    return new annotator.ui.viewer.Viewer({
      onEdit: () => {},
      onDelete: () => {},
      permitEdit: () => false,
      permitDelete: () => false,
      defaultFields: false,
      autoViewHighlights: this.element,
      extensions: [
        AnnotationContainer(this.app, this.uuid, this.gocheckVersion, this.isEditable)
      ]
    });
  }

  _createTextSelector() {
    return new TextSelector(this.element, {
      onSelection: (ranges, event) => {

        if (ranges.length === 0) {
          return;
        }

        if (
          event.target.form &&
          event.target.form.classList.contains('annotator-widget')
        ) {
          return;
        }
        const mousepoint = annotator.util.mousePosition(event);

        if (this._isInsideTargetArea(event.target)) {
          this.setInteractionPoint(mousepoint);
        } else {
          this.setInteractionPoint(this._getHighlightCoordinates(mousepoint));
        }
        this.app.annotations.create(this._makeAnnotation(ranges, this.element));
      }
    });
  }

  _getHighlightCoordinates(mousepoint) {
    const highlightedText = window.getSelection();
    const range = highlightedText.getRangeAt(0);
    const textcoordinate = range.getBoundingClientRect();
    return {
      top: mousepoint.top,
      left: textcoordinate.left + (textcoordinate.width / 4),
    };
  }

  _createEditor() {
    return new annotator.ui.editor.Editor({
      defaultFields: false,
      extensions: [
        AnnotatorContainer(this.app, this.uuid, this.gocheckVersion)
      ]
    })
  }

  _makeAnnotation(ranges, element) {
    let texts = [];
    let serializedRanges = [];
    for (const range of ranges) {
      const textRange = range.text();
      texts.push(textRange.trim());

      const serializedRange = range.serialize(element, '.annotator-hl');
      const { start, end } = getEndSpaceCounts(textRange);
      serializedRanges.push({
        ...serializedRange,
        startOffset: serializedRange.startOffset + start,
        endOffset: serializedRange.endOffset - end,
      });
    }
    return {
      quote: texts.join(' / '),
      ranges: serializedRanges,
    };
  }

  _isInsideTargetArea(element) {
    const area = document.getElementById('content');
    return area.contains(element);
  }

  preserveWhitespaces() {
    const translatedContent = document.querySelector('#content p');
    if (translatedContent) {
      translatedContent.normalize();
    }

    const annotationNodes = document.querySelectorAll('#content span[data-annotation-id]');
    for (let i = 0; i < annotationNodes.length; i++) {
      encloseAdjacentWhitespaces(annotationNodes[i]);
    }
  }

  destroy() {
    this.ui.highlighter.destroy();
    this.ui.viewer.destroy();
    if (this.isEditable) {
      this.ui.textselector.destroy();
      this.ui.editor.destroy();
    }
  }

  annotationsLoaded(annotations) {
    this.ui.highlighter.drawAll(annotations);
    this.preserveWhitespaces();
    this.onAnnotationChange();
  }

  annotationCreated(annotation) {
    this.ui.highlighter.draw(annotation);
    this.onAnnotationChange();
  }

  annotationUpdated(annotation) {
    this.ui.highlighter.redraw(annotation);
    this.onAnnotationChange();
  }

  annotationDeleted(annotation) {
    this.ui.highlighter.undraw(annotation);
    this.onAnnotationChange();
  }

  annotationClosed() {
    this.ui.viewer.hide();
    if (this.isEditable) {
      this.ui.editor.hide();
    }
    this.preserveWhitespaces();
  }

  beforeAnnotationCreated(annotation) {
    return this.ui.editor.load(annotation, this.ui.interactionPoint);
  }

  beforeAnnotationUpdated(annotation) {
    return this.ui.editor.load(annotation, this.ui.interactionPoint);
  }

  setInteractionPoint(point) {
    this.ui.interactionPoint = point;
  }
}

export default AnnotatorModule;
